'use client';
import { ChevronsRight } from 'lucide-react';
import React, { useState } from 'react';
import { useAtom } from 'jotai';
import { selectedModuleOverview } from '@/libs/atoms';
import { PortableTextBlock } from '@portabletext/react';
import { ArbitraryTypedObject } from '@portabletext/types';

export type SidebarItem = {
  order: number;
  id: number | string;
  type: string;
  content: string;
  description:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  href: any;
  icon?: any;
  iconBackground?: string;
  isActive: boolean;
};

type SidebarProps = {
  sidebarItems: SidebarItem[];
};

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

function updateSidebarItemsState(
  items: SidebarItem[],
  setSelectedModuleOverview: (type: string) => void,
  itemId: number | string
) {
  return items.map<SidebarItem>((i) => {
    if (i.id === itemId) {
      setSelectedModuleOverview(i.type);
      return { ...i, isActive: true };
    } else {
      return { ...i, isActive: false };
    }
  });
}

const ModuleOverviewSidebar = ({ sidebarItems }: SidebarProps) => {
  const [sidebarItemsState, setSidebarItemsState] =
    useState<SidebarItem[]>(sidebarItems);
  const [, setSelectedModuleOverview] = useAtom(selectedModuleOverview);

  const handleClick = (
    event: React.MouseEvent<HTMLSpanElement>,
    item: SidebarItem
  ) => {
    event.preventDefault();
    const newItems = updateSidebarItemsState(
      sidebarItemsState,
      setSelectedModuleOverview,
      item.id
    );
    setSidebarItemsState(newItems);
  };

  return (
    <div className="flow-root md:mt-10 xl:mt-24 md:sticky top-[150px]">
      <h3 className="text-base lg:text-lg md:w-[100%] xl:w-[80%] pb-8">
        Please Click below to explore
        <br />
        the Simplicity<sup>®</sup>{' '}
        <span className="text-primary drop-shadow-md font-medium">Sectors</span>
      </h3>
      <ul role="list" className="-mb-8">
        {sidebarItemsState.map((item, itemIdx) => (
          <li key={item.id}>
            <div className="relative pb-8">
              {itemIdx !== sidebarItems.length - 1 ? (
                <span
                  className="absolute left-2 top-4 -ml-px h-full w-0.5 bg-gray-300"
                  aria-hidden="true"
                />
              ) : null}
              <span
                className="relative flex space-x-1 lg:space-x-3 cursor-pointer"
                onClick={(e) => handleClick(e, item)}
              >
                <div>
                  <span
                    className={classNames(
                      item.isActive
                        ? 'bg-primary ring-primary'
                        : 'bg-white ring-gray-300',
                      'flex h-4 w-4 items-center justify-center rounded-full ring-1 '
                    )}
                  ></span>
                </div>
                <div className="flex min-w-0 flex-1 justify-between space-x-4 flex-row">
                  <p
                    className={`flex flex-row justify-between w-full gap-x-2 lg:gap-x-6 text-sm text-gray-500 ${
                      item.isActive ? 'font-semibold' : 'font-normal'
                    }`}
                  >
                    <span className="w-[130px] lg:w-auto">{item.content}</span>
                    <ChevronsRight
                      className={`text-lg ${item.isActive ? 'text-primary' : 'text-gray-300'}`}
                      size="22"
                    />
                  </p>
                </div>
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ModuleOverviewSidebar;
